import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Menu } from "@headlessui/react";
import { FaUserCircle, FaGoogle, FaHeart, FaBars, FaTimes, FaTrophy, FaSortAlphaDown, FaGamepad } from "react-icons/fa";
import { Award } from "lucide-react";

const Header = ({ toggleDark, isDark }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loginWithGoogle, fetchFavorites } = useAuth();
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    const updateCount = async () => {
      if (user) {
        const favs = await fetchFavorites();
        setFavoritesCount(favs.length);
      } else {
        setFavoritesCount(0);
      }
    };
    updateCount();
    
    // Set active tab based on current location and query params
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    if (path === "/") setActiveTab("");
    else if (path === "/categories") {
      const sort = searchParams.get('sort');
      const filter = searchParams.get('filter');
      
      if (sort === 'title') {
        setActiveTab("a-z");
      } else if (filter === 'goty') {
        setActiveTab("goty");
      } else {
        setActiveTab("browse");
      }
    }
    else if (path === "/goty") setActiveTab("goty");
    else if (path === "/favorites") setActiveTab("favorites");
  }, [user, fetchFavorites, location]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
    setActiveTab("");
  };

  const handleFavoritesClick = (e) => {
    e.preventDefault();
    if (user) {
      navigate("/favorites");
      setActiveTab("favorites");
      setIsMenuOpen(false);
    } else {
      setShowLoginPrompt(true);
    }
  };

  const handleBrowseClick = () => {
    navigate("/categories");
    setActiveTab("browse");
    setIsMenuOpen(false);
  };

  const handleAzClick = () => {
    navigate("/categories?sort=title&view=a-z");
    setActiveTab("a-z");
    setIsMenuOpen(false);
  };

  const handleGotyClick = () => {
    navigate("/goty");
    setActiveTab("goty");
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-gradient-to-b from-gray-800 to-gray-900/95 backdrop-blur-lg border-b border-gray-700/50 shadow-2xl z-50">
        <div className="container mx-auto px-4">
          {/* Top Bar */}
          <div className="flex items-center justify-between h-20">
            {/* Logo - UPDATED: Clean design without box and tick */}
            <Link
              to="/"
              onClick={handleLogoClick}
              className="flex items-center gap-3 text-white relative z-10 cursor-pointer group"
              title="Home"
            >
              {/* Logo Container with subtle glow effect */}
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-red-600 via-red-500 to-red-600 rounded-lg blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                <div className="relative p-1">
                  {/* GameHub Logo Image */}
                  <div className="h-12 w-32 rounded-lg flex items-center justify-center">
                    <img 
                      src="/gamehub-logo.png" 
                      alt="GameHub Logo" 
                      className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                      onError={(e) => {
                        // Fallback in case image doesn't load
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `
                          <div class="h-full w-full bg-gradient-to-r from-red-600 to-red-800 rounded-lg flex items-center justify-center shadow-inner">
                            <span class="text-white font-bold text-2xl tracking-widest">GAMEHUB</span>
                          </div>
                        `;
                      }}
                    />
                  </div>
                </div>
              </div>
            </Link>

            {/* Navigation Tabs - Desktop */}
            <div className="hidden lg:flex items-center gap-2 relative z-10">
              {/* Browse Games Tab */}
              <button
                onClick={handleBrowseClick}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === "browse" 
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg border border-blue-500" 
                  : "bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-gray-300 hover:text-white border border-gray-700 hover:border-blue-500/50"}`}
              >
                <FaGamepad className="w-4 h-4" />
                <span>Browse Games</span>
              </button>
              
              {/* A-Z Tab */}
              <button
                onClick={handleAzClick}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === "a-z" 
                  ? "bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg border border-purple-500" 
                  : "bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-gray-300 hover:text-white border border-gray-700 hover:border-purple-500/50"}`}
              >
                <FaSortAlphaDown className="w-4 h-4" />
                <span>A-Z</span>
              </button>
              
              {/* GOTY Tab */}
              <button
                onClick={handleGotyClick}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === "goty" 
                  ? "bg-gradient-to-r from-yellow-600 to-yellow-700 text-white shadow-lg border border-yellow-500" 
                  : "bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-gray-300 hover:text-white border border-gray-700 hover:border-yellow-500/50"}`}
              >
                <FaTrophy className="w-4 h-4" />
                <span>GOTY Edition</span>
                <span className="text-xs px-2 py-0.5 bg-gradient-to-r from-yellow-800 to-yellow-900 rounded-full text-yellow-200">NEW</span>
              </button>
            </div>

            {/* Right Side Actions - Removed Dark Mode Toggle */}
            <div className="flex items-center space-x-3">
              {/* Favorites Button with Counter */}
              <div className="relative group">
                <button
                  onClick={handleFavoritesClick}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${activeTab === "favorites"
                    ? "bg-gradient-to-r from-pink-600 to-pink-700 text-white shadow-lg border border-pink-500"
                    : "bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-gray-300 hover:text-white border border-gray-700 hover:border-pink-500/50"}`}
                  title="My Favorites"
                >
                  <FaHeart className="w-4 h-4" />
                  <span className="hidden sm:inline">Favorites</span>
                  {favoritesCount > 0 && (
                    <span className="text-xs px-2 py-0.5 bg-gradient-to-r from-pink-700 to-pink-800 rounded-full text-white">
                      {favoritesCount}
                    </span>
                  )}
                </button>
                <div className="absolute top-full mt-2 right-0 w-48 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 rounded-lg p-3 shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <div className="text-xs text-gray-400 mb-2">Saved Games</div>
                  <div className="text-sm text-white">{favoritesCount} games</div>
                  <div className="text-xs text-gray-500">Click to view</div>
                </div>
              </div>

              {/* Account Menu */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 rounded-xl border border-red-600 text-white transition-all shadow-lg">
                  <FaUserCircle size={20} className="text-white" />
                  <span className="hidden sm:inline max-w-[100px] truncate font-medium">
                    {user ? user.displayName?.split(' ')[0] || user.email?.split('@')[0] : "Account"}
                  </span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Menu.Button>
                <Menu.Items className="absolute right-0 mt-2 w-64 rounded-xl shadow-2xl bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 text-white overflow-visible z-50">
                  {user ? (
                    <div className="p-3 flex flex-col gap-2">
                      <div className="px-3 py-3 border-b border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-red-700 rounded-full flex items-center justify-center">
                            <span className="font-bold text-white">
                              {user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-bold text-white truncate">{user.displayName || user.email}</p>
                            <p className="text-xs text-gray-400 truncate">{user.email}</p>
                          </div>
                        </div>
                      </div>
                      
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/account"
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg ${active ? "bg-gray-700" : ""}`}
                          >
                            <FaUserCircle className="w-5 h-5" />
                            <span>My Profile</span>
                          </Link>
                        )}
                      </Menu.Item>
                      
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleFavoritesClick}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left ${active ? "bg-gray-700" : ""}`}
                          >
                            <FaHeart className="w-5 h-5 text-pink-400" />
                            <span>My Favorites</span>
                            <span className="ml-auto text-xs bg-gradient-to-r from-pink-700 to-pink-800 text-white rounded-full px-2 py-1">
                              {favoritesCount}
                            </span>
                          </button>
                        )}
                      </Menu.Item>
                      
                      <div className="border-t border-gray-700 my-2"></div>
                      
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={logout}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left ${active ? "bg-red-900/30" : ""}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span className="text-red-400">Logout</span>
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  ) : (
                    <div className="p-4 flex flex-col gap-3">
                      <div className="text-center mb-2">
                        <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-r from-red-600 to-red-700 rounded-full flex items-center justify-center">
                          <FaUserCircle className="w-6 h-6 text-white" />
                        </div>
                        <p className="text-sm text-gray-300">Sign in to access all features</p>
                      </div>
                      
                      <button
                        onClick={loginWithGoogle}
                        className="flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg text-white font-medium transition-all shadow-lg"
                      >
                        <FaGoogle className="w-5 h-5" /> Login with Google
                      </button>
                      
                      <div className="text-center text-xs text-gray-500 pt-2">
                        No account? Sign up in seconds
                      </div>
                    </div>
                  )}
                </Menu.Items>
              </Menu>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-3 rounded-xl bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-gray-300 hover:text-white border border-gray-700 transition-all"
              >
                {isMenuOpen ? <FaTimes className="h-5 w-5" /> : <FaBars className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-700/50 bg-gradient-to-b from-gray-800 to-gray-900/95 backdrop-blur-lg">
            <div className="container mx-auto px-4 py-4">
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={handleBrowseClick}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${activeTab === "browse" 
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white border border-blue-500" 
                    : "bg-gradient-to-r from-gray-700 to-gray-800 border border-gray-700 text-gray-300 hover:border-blue-500/50"}`}
                >
                  <FaGamepad className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">Browse</span>
                </button>
                
                <button
                  onClick={handleAzClick}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${activeTab === "a-z" 
                    ? "bg-gradient-to-r from-purple-600 to-purple-700 text-white border border-purple-500" 
                    : "bg-gradient-to-r from-gray-700 to-gray-800 border border-gray-700 text-gray-300 hover:border-purple-500/50"}`}
                >
                  <FaSortAlphaDown className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">A-Z</span>
                </button>
                
                <button
                  onClick={handleGotyClick}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${activeTab === "goty" 
                    ? "bg-gradient-to-r from-yellow-600 to-yellow-700 text-white border border-yellow-500" 
                    : "bg-gradient-to-r from-gray-700 to-gray-800 border border-gray-700 text-gray-300 hover:border-yellow-500/50"}`}
                >
                  <FaTrophy className="w-6 h-6 mb-2" />
                  <span className="text-sm font-medium">GOTY</span>
                </button>
              </div>
              
              {/* Mobile Account Section */}
              <div className="mt-4 pt-4 border-t border-gray-700/50">
                {user ? (
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl">
                    <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-red-700 rounded-full flex items-center justify-center">
                      <span className="font-bold text-white text-lg">
                        {user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-white text-sm">{user.displayName || user.email?.split('@')[0]}</p>
                      <p className="text-xs text-gray-400">Premium Member</p>
                    </div>
                    <button
                      onClick={logout}
                      className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300 text-xs rounded-lg"
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={loginWithGoogle}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 rounded-xl text-white font-medium shadow-lg"
                  >
                    <FaGoogle className="w-5 h-5" /> Login with Google
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[60] animate-fadeIn"
          onClick={() => setShowLoginPrompt(false)}
        >
          <div
            className="bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 p-8 rounded-2xl shadow-2xl max-w-sm w-full mx-4 transform animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-red-600 to-red-700 rounded-full flex items-center justify-center">
                <FaHeart className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-white">
                Unlock Favorites
              </h2>
              <p className="text-gray-400 text-sm">
                Login to save and manage your favorite games
              </p>
            </div>
            <div className="space-y-4">
              <button
                onClick={() => {
                  loginWithGoogle();
                  setShowLoginPrompt(false);
                  setIsMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-xl text-white font-bold transition-all shadow-lg"
              >
                <FaGoogle className="w-6 h-6" /> Continue with Google
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="w-full px-6 py-4 bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-700 hover:to-gray-800 rounded-xl text-gray-300 transition-all border border-gray-700"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;